export const PROFILE_BIO_INDEXING_WORD_FLOOR = 120;

export interface ProfileBioLineBreakRepair {
  value: string;
  repairedBreaks: number;
}

const SENTENCE_ENDING_CHARACTER = /[.!?:"”](?:['"”’)\]]*)$/u;
const LOWERCASE_LINE_START = /^(?:\s|['"“‘(]|\[)*\p{Ll}/u;
const LIST_LINE_START = /^(?:[-*•–—]|\d+[.)])\s/u;
const TITLE_CONNECTORS = new Set([
  'a',
  'an',
  'and',
  'at',
  'by',
  'for',
  'from',
  'in',
  'of',
  'on',
  'or',
  'the',
  'to',
  'with'
]);

function isTitleLikeShortLine(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed || trimmed.length > 100) {
    return false;
  }

  const words = trimmed.split(/\s+/).filter(word => /[\p{L}\p{N}]/u.test(word));
  if (words.length === 0 || words.length > 12) {
    return false;
  }

  return words.every((word) => {
    const normalized = word.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
    if (!normalized) {
      return true;
    }
    if (TITLE_CONNECTORS.has(normalized.toLocaleLowerCase('en-US'))) {
      return true;
    }
    return /^[\p{Lu}\p{N}]/u.test(normalized);
  });
}

function shouldJoinBioLines(previousLine: string, nextLine: string): boolean {
  const previous = previousLine.trimEnd();
  const next = nextLine.trimStart();

  if (!previous || !next || LIST_LINE_START.test(next)) {
    return false;
  }

  // Short title blocks are intentional formatting, not prose wraps. This
  // protects profile headers such as Jeff Schlesinger's two authored role
  // lines before applying the more general wrap-repair rule.
  if (isTitleLikeShortLine(previous) && isTitleLikeShortLine(next)) {
    return false;
  }

  return !SENTENCE_ENDING_CHARACTER.test(previous) || LOWERCASE_LINE_START.test(next);
}

export function repairProfileBioLineBreakArtifacts(value: string): ProfileBioLineBreakRepair {
  if (!/[\r\n]/u.test(value)) {
    return { value, repairedBreaks: 0 };
  }

  const parts = value.split(/(\r\n|\n|\r)/u);
  let repairedValue = parts[0] ?? '';
  let repairedBreaks = 0;

  for (let index = 1; index < parts.length; index += 2) {
    const separator = parts[index] ?? '';
    const nextLine = parts[index + 1] ?? '';
    const previousBreakIndex = Math.max(
      repairedValue.lastIndexOf('\n'),
      repairedValue.lastIndexOf('\r')
    );
    const previousLine = repairedValue.slice(previousBreakIndex + 1);

    if (shouldJoinBioLines(previousLine, nextLine)) {
      repairedValue = `${repairedValue.replace(/[ \t]+$/u, '')} ${nextLine.replace(/^[ \t]+/u, '')}`;
      repairedBreaks += 1;
    } else {
      repairedValue += `${separator}${nextLine}`;
    }
  }

  return { value: repairedValue, repairedBreaks };
}

export function countProfileBioWords(bio: string | null | undefined): number {
  const normalized = bio?.trim() ?? '';
  return normalized ? normalized.split(/\s+/).length : 0;
}
